import { Link } from "react-router-dom";
import { ArrowUpRight, Users, Heart, ShieldCheck, Lightbulb, Leaf, Handshake } from "lucide-react";
import { RevealText, Reveal } from "@/components/site/Reveal";
import { COMPANY } from "@/lib/assets";

const HERO_IMG = "https://images.unsplash.com/photo-1556761175-b413da4baf72?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";
const SPLIT_IMG = "https://images.unsplash.com/photo-1664382953403-fc1ac77073a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const VALUES = [
  { icon: Users, t: "People", d: "Our greatest strength—empowered, valued, and united to move the world forward." },
  { icon: Heart, t: "Customer-First", d: "We listen, adapt, and deliver with precision." },
  { icon: ShieldCheck, t: "Integrity", d: "We act with transparency and accountability." },
  { icon: Lightbulb, t: "Innovation", d: "We challenge the status quo and embrace smart solutions." },
  { icon: Leaf, t: "Sustainability", d: "We take responsibility for our impact—on people, planet, and processes." },
  { icon: Handshake, t: "Teamwork", d: "We collaborate globally and act locally, driven by a shared purpose." },
];

export default function Career() {
  return (
    <div data-testid="career-page">
      <section className="relative pt-40 pb-20 mx-auto max-w-[1600px] px-5 sm:px-8">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-6">/ Career</p>
        <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-[13vw] sm:text-[10vw] lg:text-[8vw]">
          <RevealText lines={["Join our", "team."]} testid="page-title" />
        </h1>
      </section>

      <section className="relative h-[65vh] overflow-hidden">
        <img src={HERO_IMG} alt="Freight IQ team collaborating in the office" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
      </section>

      {/* Intro */}
      <section className="mx-auto max-w-[1600px] px-5 sm:px-8 py-28 grid lg:grid-cols-12 gap-16">
        <Reveal className="lg:col-span-5">
          <h2 className="font-display text-3xl sm:text-5xl uppercase tracking-tighter leading-[0.9]">Our people are our greatest strength.</h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-7 space-y-6 text-white/60 text-lg leading-relaxed">
          <p>At {COMPANY.legal}, we believe our people are our greatest strength. We're always looking for passionate, driven individuals who are ready to make an impact. Our culture is built on a foundation of respect, innovation, and an entrepreneurial mindset. If you're ready to grow your career and help shape the future, we want to hear from you.</p>
          <p className="text-white/80">If you resonate with our values, we invite you to join our journey.</p>
        </Reveal>
      </section>

      {/* Values / What we look for */}
      <section data-testid="career-values" className="border-y border-white/10 bg-[#080808]">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-24">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">/ Our Values</p>
            <h2 className="font-display text-4xl sm:text-6xl uppercase tracking-tighter leading-[0.9]">What we stand for.</h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {VALUES.map((v, i) => (
              <Reveal key={v.t} delay={(i % 3) * 0.06} className="bg-[#080808] p-10">
                <span className="h-11 w-11 grid place-items-center border border-primary/40 text-primary mb-6">
                  <v.icon size={20} />
                </span>
                <h3 className="font-display text-2xl uppercase tracking-tight">{v.t}</h3>
                <p className="text-white/60 mt-3">{v.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Apply split */}
      <section className="mx-auto max-w-[1600px] px-5 sm:px-8 py-28 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <div className="relative h-[420px] overflow-hidden border border-white/10 group">
            <img src={SPLIT_IMG} alt="Colleagues walking through a warehouse" className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter">Ready to make an impact?</h2>
          <p className="text-white/60 text-lg mt-5 max-w-xl">
            Send us your resume and a note about why you'd like to join Freight IQ. Our team reviews every application and will reach out if there's a fit.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href={`mailto:${COMPANY.email}?subject=Career%20Application%20-%20Freight%20IQ`} data-testid="career-apply-btn" className="inline-flex items-center gap-3 hover-fill px-8 py-4 font-mono text-xs uppercase tracking-widest">
              Apply Now <ArrowUpRight size={16} />
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-widest hover:border-primary hover:text-primary transition-colors duration-300">
              Contact Us
            </Link>
          </div>
          <p className="mt-6 font-mono text-xs uppercase tracking-widest text-white/40">{COMPANY.email}</p>
        </Reveal>
      </section>
    </div>
  );
}
